import { AppResourcePage } from './app-resource-base';
import { branchSwitchOnResource } from './helpers/branch-switch';
import { cache } from '../cache-impl/cache-registry';
import { checkSuppliersIsOnline } from '../quotes/quote-creator';
import { containsFlag, isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';
import { createConcurrencyCheck, ResourceLocker } from '../common/resource-lock';
import { createQuoteView, getQuoteContainer, getQuoteContainerManager, QuoteLock } from '../quotes/quote-ui-adapter';
import { customElement } from 'lit/decorators.js';
import { EAbort } from '../../../webmodule-common/other/abort';
import { FranchiseeQuoteContainerManager } from '../quotes/data/franchisee-quote-manager';
import { getQuoteTitleStr } from '../../quotes/data/quote-helper-functions';
import { goStaticURL } from '../../../webmodule-common/other/ui/resource-resolver';
import { information } from '../../../webmodule-common/other/ui/modal-option';
import { LangAppQuotePage } from '../../../localization/scripts/dealer-franchisee/pages/app-quote-lang';
import { ModalViewBase } from '../../../webmodule-common/other/ui/data-entry-screen-base';
import { NullPromise } from '../../../webmodule-common/interop/types/misc';
import { QuoteLockState } from '../../api/dealer-api-interface-all';
import { QuoteState } from '../../api/dealer-api-interface-quote';
import { v6BootLoader } from '../../v6config/v6config';

@customElement('app-quote')
export class AppQuotePage extends AppResourcePage {
  qm?: FranchiseeQuoteContainerManager;

  protected override isResourceReadonly(): boolean {
    // Deliberately NOT isReadonly(): that folds in "somebody else holds the lock", which this method's
    // contract explicitly excludes. Including it made the header badge short-circuit to a bare
    // "Readonly" and drop the "Currently in use by {name}" branch, so a user shut out of a quote could
    // not see who had it — while the project page, which does not override this, showed the name.
    return this.qm?.isReadonlyIgnoringForeignLock() ?? false;
  }

  async createModalEditor(id: string): NullPromise<ModalViewBase> {
    if (!(await v6BootLoader())) {
      await information(LangAppQuotePage.supplierOfflineMsg());
      this.forcedReadonly = true;
      this.forcedReadonlyMsg = LangAppQuotePage.supplierOffline();
    }

    const container = getQuoteContainer(id);
    if (isEmptyOrSpace(id)) id = container.quoteId;

    this.qm = getQuoteContainerManager(container, this.lock) as FranchiseeQuoteContainerManager;
    const qm = this.qm;
    if (!(await qm.needsQuote())) return null;
    if (containsFlag(qm.quote.state, QuoteState.Copying)) {
      await information(LangAppQuotePage.cannotOpenWhileCopying());
      throw new EAbort();
    }

    // Expose currency to globalThis for external libraries (V6 Configurator)
    (globalThis as any).externalLibraryCurrency.currency = qm.quoteCurrency;

    if (!(await branchSwitchOnResource(qm.branchQuote.branchId, LangAppQuotePage.quoteGlossary()))) return null;
    if (!(await checkSuppliersIsOnline(this.qm.quote.supplierId))) {
      await information(LangAppQuotePage.supplierOfflineMsg());
      this.forcedReadonly = true;
      this.forcedReadonlyMsg = LangAppQuotePage.supplierOffline();
    }
    // Consult the lock state on OPEN, not only on refresh. Wiring this into resourceRefreshing alone
    // meant opening a quote that was ALREADY the source of an operation gave a fully editable view:
    // the list badge correctly said `Copying`, but nothing had consulted the lock on this page yet, so
    // the user could edit until the server refused the save. An operation slow enough to notice is
    // exactly when someone has time to open the source, so that is the likely case, not the rare one.
    const lockState = await this.operationLockState();

    // A delete REFUSES entry rather than granting read-only. Opening a record that is on its way out
    // is pointless — there is nothing to read that will still exist, and the row vanishes underneath
    // the reader. A copy is different: the quote survives, so read-only is genuine access.
    //
    // Note this cannot be done with QuoteState the way the copy check above is: `QuoteState.Copying`
    // sits on the copy's TARGET, whereas a delete concerns the quote itself. Introducing a committed
    // `Deleting` state to mirror it would need an enum value, transitions, every state filter and
    // report, and a two-phase commit whose failure mode is a quote STRANDED in Deleting. The
    // operation lock already carries this, and is released in a finally.
    if (lockState === QuoteLockState.Deleting) {
      await information(LangAppQuotePage.cannotOpenWhileDeleting());
      throw new EAbort();
    }

    this.qm.forceLockout = this.forcedReadonly || lockState === QuoteLockState.Copying;

    // The copy handover: this page owns the interactive lock, so it is the only place that can hand
    // it over when the manager copies the quote we are holding open (requesting supplier review does
    // exactly that). Releasing our ResourceLocker drops it out of the refresh loop — so it cannot be
    // silently re-acquired within the minute — and sets isLockOwner false, which by design makes the
    // open view read-only rather than leaving it looking editable over a quote we no longer hold.
    qm.standDownFromEditing = async () => {
      await this.lock?.release();
      this.requestUpdate();
    };

    return await createQuoteView(qm);
  }

  async redirectPath(): NullPromise<string> {
    return '/quotes';
  }

  meta() {
    return {
      title: () => LangAppQuotePage.quoteEditorTitle({ quoteTitle: getQuoteTitleStr(this.qm?.quote, undefined, true) }),
      description: 'SoftTech dealer CPQ'
    };
  }

  protected async resourceRefreshing() {
    await super.resourceRefreshing();
    if (this.qm?.quote.id) {
      await cache().projectResourceLink.updateLocal(this.qm?.quote.id);
      await cache().quote.updateLocal(this.qm?.quote.id);
      await this.applyOperationLockReadonly();
    }
  }

  /**
   * The operation (copy/delete) lock currently held on this quote, or None.
   *
   * getData, not getLocalData: the quote cache flushes itself on the SSE that announces a lock
   * change, so the local copy is expired at exactly the moment the new value is wanted.
   */
  private async operationLockState(): Promise<QuoteLockState> {
    if (!this.qm?.quote.id) return QuoteLockState.None;
    const summary = (await cache().quote.getData(this.qm.quote.id))?.quoteSummary;
    return summary?.lockState ?? QuoteLockState.None;
  }

  /**
   * Go READ-ONLY while a copy or delete of this quote is in flight, and come back when it finishes.
   *
   * Deliberately not a refusal HERE: this is the refresh path, so the user is already inside the
   * editor when the operation starts, and throwing them out mid-session is worse than making the view
   * read-only. Refusing ENTRY to a quote being deleted happens on the open path instead
   * (createModalEditor), where there is nothing to throw the user out of.
   *
   * The copy pushes an SSE for its source at both start and finish, so the view flips both ways on
   * its own. Without this the editor would look fully editable during someone else's copy and the
   * save would be refused by the server's update guard — after the user had done the work.
   */
  private async applyOperationLockReadonly() {
    if (!this.qm?.quote.id) return;

    const summary = (await cache().quote.getData(this.qm.quote.id))?.quoteSummary;
    const operationInProgress =
      summary?.lockState === QuoteLockState.Copying || summary?.lockState === QuoteLockState.Deleting;

    // Only ever RAISE lockout from here. forcedReadonly has other owners (supplier offline), and
    // clearing it wholesale would silently re-enable editing they had disabled.
    const shouldLockOut = this.forcedReadonly || operationInProgress;
    if (this.qm.forceLockout !== shouldLockOut) {
      this.qm.forceLockout = shouldLockOut;
      this.requestUpdate();
    }
  }

  protected createLock(id: string): ResourceLocker | undefined {
    const lockRevokedEvent = async () => {
      await this.view?.abandonAndClose(true);
      goStaticURL('/quotes');
    };

    // Create concurrency check that ensures local data is loaded and compares recordVersion with server
    const concurrencyCheck = createConcurrencyCheck('QUOTE.QUOTE', id, async () => {
      await this.qm?.needsQuote();
      return this.qm?.quote?.recordVersion ?? '';
    });

    return new QuoteLock(id, lockRevokedEvent, concurrencyCheck);
  }
}
